import { useRef, useState } from 'react';
import {
  Box, Typography, Stack, Chip, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, alpha, useTheme,
} from '@mui/material';
import {
  Visibility, CheckCircle, Cancel, Description, PictureAsPdf, ImageOutlined, Upload, OpenInNew,
} from '@mui/icons-material';
import { resolveAssetUrl } from '../../utils/assetUrl';

export type DocumentStatusKey = 'NOTUPLOADED' | 'WAITINGFORAPPROVAL' | 'APPROVED' | 'DENIED' | 'EXPIRED';

const STATUS_CHIP: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'default' }> = {
  APPROVED: { label: 'Approved', color: 'success' },
  WAITINGFORAPPROVAL: { label: 'Pending review', color: 'warning' },
  DENIED: { label: 'Denied', color: 'error' },
  EXPIRED: { label: 'Expired', color: 'error' },
  NOTUPLOADED: { label: 'Not uploaded', color: 'default' },
};

interface PartnerDocumentCardProps {
  config: {
    _id: string;
    name: string;
    isMandatory?: boolean;
    identifierRequired?: boolean;
    expiryRequired?: boolean;
  };
  uploaded?: {
    _id?: string;
    fileUrl?: string;
    documentNumber?: string;
    expiryDate?: string;
    rejectionReason?: string;
  };
  documentStatus: DocumentStatusKey;
  loading?: boolean;
  uploading?: boolean;
  onVerifyRequest: (action: 'approved' | 'denied') => void;
  onUpload?: (file: File, extras: { documentNumber?: string; expiryDate?: string }) => void;
}

function isPdf(url?: string) {
  return !!url && /\.pdf(\?|$)/i.test(url);
}

function todayIsoDate() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function isExpiryDatePast(value?: string) {
  if (!value) return false;
  return value.slice(0, 10) < todayIsoDate();
}

export default function PartnerDocumentCard({
  config, uploaded, documentStatus, loading, uploading, onVerifyRequest, onUpload,
}: PartnerDocumentCardProps) {
  const theme = useTheme();
  const fileRef = useRef<HTMLInputElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [documentNumber, setDocumentNumber] = useState(uploaded?.documentNumber || '');
  const [expiryDate, setExpiryDate] = useState(uploaded?.expiryDate?.slice(0, 10) || '');

  const chip = STATUS_CHIP[documentStatus] || STATUS_CHIP.NOTUPLOADED;
  const isPending = documentStatus === 'WAITINGFORAPPROVAL';
  const isApproved = documentStatus === 'APPROVED';
  const isDenied = documentStatus === 'DENIED';
  const isExpired = documentStatus === 'EXPIRED';
  const needsReupload = isExpired || isDenied;
  const fileUrl = uploaded?.fileUrl ? resolveAssetUrl(uploaded.fileUrl) : '';
  const hasFile = !!fileUrl;
  const pdf = isPdf(fileUrl);

  const accent =
    isPending ? theme.palette.warning.main
      : isApproved ? theme.palette.success.main
        : isDenied || isExpired ? theme.palette.error.main
          : theme.palette.divider;

  const openUpload = () => {
    setDocumentNumber(uploaded?.documentNumber || '');
    setExpiryDate(isExpired ? '' : (uploaded?.expiryDate?.slice(0, 10) || ''));
    setPendingFile(null);
    setUploadOpen(true);
  };

  const expiryInPast = isExpiryDatePast(expiryDate);
  const uploadLabel = isExpired || isDenied ? 'Re-upload' : hasFile ? 'Replace' : 'Upload';

  const submitUpload = () => {
    if (!pendingFile || !onUpload) return;
    onUpload(pendingFile, {
      documentNumber: documentNumber.trim() || undefined,
      expiryDate: expiryDate || undefined,
    });
    setUploadOpen(false);
    setPendingFile(null);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: 1.5,
          p: 2,
          borderRadius: 2,
          border: 1,
          borderColor: isPending || isExpired ? alpha(isExpired ? theme.palette.error.main : theme.palette.warning.main, 0.35) : 'divider',
          bgcolor: isPending
            ? alpha(theme.palette.warning.main, 0.04)
            : isExpired
              ? alpha(theme.palette.error.main, 0.04)
              : 'background.paper',
          borderTop: `3px solid ${accent}`,
        }}
      >
        <Box
          onClick={hasFile ? () => setPreviewOpen(true) : openUpload}
          sx={{
            width: '100%',
            height: 140,
            flexShrink: 0,
            borderRadius: 1.5,
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            cursor: 'pointer',
          }}
        >
          {hasFile && !pdf ? (
            <Box component="img" src={fileUrl} alt={config.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : hasFile ? (
            <PictureAsPdf sx={{ fontSize: 40, color: 'error.main' }} />
          ) : (
            <>
              <Upload sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="caption" color="text.secondary">Click to upload</Typography>
            </>
          )}
        </Box>

        <Box flex={1} minWidth={0} display="flex" flexDirection="column">
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1} mb={0.5}>
            <Box minWidth={0}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>{config.name}</Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                <Chip size="small" label={config.isMandatory ? 'Required' : 'Optional'} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                {config.identifierRequired && <Chip size="small" label="ID #" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
                {config.expiryRequired && <Chip size="small" label="Expiry" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
              </Stack>
            </Box>
            <Chip size="small" label={chip.label} color={chip.color} />
          </Stack>

          {(uploaded?.documentNumber || uploaded?.expiryDate) && (
            <Typography
              variant="caption"
              color={isExpired ? 'error.main' : 'text.secondary'}
              display="block"
              sx={{ mt: 0.5, fontWeight: isExpired ? 700 : 400 }}
            >
              {uploaded.documentNumber ? `No. ${uploaded.documentNumber}` : ''}
              {uploaded.documentNumber && uploaded.expiryDate ? '  ·  ' : ''}
              {uploaded.expiryDate
                ? `${isExpired ? 'Expired' : 'Expires'} ${new Date(uploaded.expiryDate).toLocaleDateString()}`
                : ''}
            </Typography>
          )}

          {isExpired && (
            <Alert severity="error" sx={{ mt: 1, py: 0, borderRadius: 1.5 }}>
              Expired — re-upload a valid copy for review.
            </Alert>
          )}

          {isDenied && uploaded?.rejectionReason && (
            <Alert severity="error" sx={{ mt: 1, py: 0, borderRadius: 1.5 }}>
              {uploaded.rejectionReason}
            </Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 'auto', pt: 1.25 }}>
            {onUpload && (
              <Button
                size="small"
                variant={needsReupload || !hasFile ? 'contained' : 'outlined'}
                color={needsReupload ? 'error' : 'primary'}
                startIcon={<Upload />}
                disabled={uploading}
                onClick={openUpload}
              >
                {uploadLabel}
              </Button>
            )}
            {hasFile && (
              <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => setPreviewOpen(true)}>
                View
              </Button>
            )}
            {!isApproved && !isExpired && hasFile && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                disabled={loading}
                onClick={() => onVerifyRequest('approved')}
              >
                Approve
              </Button>
            )}
            {!isDenied && !isExpired && hasFile && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                disabled={loading}
                onClick={() => onVerifyRequest('denied')}
              >
                Deny
              </Button>
            )}
          </Stack>
        </Box>
      </Box>

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{uploadLabel} {config.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button variant="outlined" component="label" startIcon={<Upload />}>
              {pendingFile ? pendingFile.name : 'Choose file'}
              <input
                ref={fileRef}
                hidden
                type="file"
                accept="image/*,.pdf,application/pdf"
                onChange={(e) => setPendingFile(e.target.files?.[0] || null)}
              />
            </Button>
            <TextField
              size="small"
              label={config.identifierRequired ? 'Document number *' : 'Document number'}
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label={config.expiryRequired || isExpired ? 'Expiry date *' : 'Expiry date'}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: todayIsoDate() }}
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              error={Boolean(expiryDate && expiryInPast)}
              helperText={expiryDate && expiryInPast ? 'Pick a future expiry date' : undefined}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !pendingFile
              || uploading
              || (config.identifierRequired && !documentNumber.trim())
              || ((config.expiryRequired || isExpired) && !expiryDate)
              || expiryInPast
            }
            onClick={submitUpload}
          >
            {uploadLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Description fontSize="small" color="primary" />
          {config.name}
        </DialogTitle>
        <DialogContent>
          {pdf ? (
            <Box component="iframe" src={fileUrl} sx={{ width: '100%', height: 520, border: 0, borderRadius: 1 }} title={config.name} />
          ) : (
            <Box
              component="img"
              src={fileUrl}
              alt={config.name}
              sx={{ width: '100%', maxHeight: 520, objectFit: 'contain', borderRadius: 1, bgcolor: 'action.hover' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          {fileUrl && (
            <Button component="a" href={fileUrl} target="_blank" rel="noreferrer" startIcon={<OpenInNew />}>
              Open
            </Button>
          )}
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
